import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router"
import Button from "react-bootstrap/Button"
import { FiExternalLink } from "react-icons/fi"

import useT from "utils/hooks/useT"
import { openPresignedFile } from "utils/s3Download"
import { limsSpecVersions, limsWorklistTemplates, limsSstTemplates, limsForskrifter, message } from "state_management"
import { KINDS } from "state_management/lims/limsChangeControl"
import { SpecVersionDiff } from "components/Config/AnalysisConfigurationPage/SpecChangeControl"

// What the signer is about to sign (SPEC-058 B): the content of the pending version, per family,
// so approval never happens blind. The queue endpoint carries metadata only; the content comes
// from each family's own slice (fetched by the register on mount; the spec versions per
// definition on expand, because that GET is per definition).
export default function VersionDetail({ item }) {
  const { t } = useT("common")
  const navigate = useNavigate()

  return (
    <div className="LimsChangeControl-detail">
      {item.kind === KINDS.SPEC && <SpecDetail item={item} />}
      {item.kind === KINDS.WORKLIST && <WorklistDetail item={item} />}
      {item.kind === KINDS.SST && <SstDetail item={item} />}
      {item.kind === KINDS.FORSKRIFT && <ForskriftDetail item={item} />}
      {item.analysisDefinitionId && (
        <Button
          variant="link"
          size="sm"
          className="LimsChangeControl-open-definition"
          onClick={() => navigate(`/lims/analysis-definition/${item.analysisDefinitionId}`)}
        >
          <FiExternalLink /> {t("limsChangeControlOpenDefinition")}
        </Button>
      )}
    </div>
  )
}

function SpecDetail({ item }) {
  const dispatch = useDispatch()
  const versions = useSelector((state) => limsSpecVersions.selectVersionsForDefinition(state, item.analysisDefinitionId))

  useEffect(() => {
    dispatch(limsSpecVersions.fetch(item.analysisDefinitionId))
  }, [dispatch, item.analysisDefinitionId])

  const index = versions.findIndex((version) => version.id === item.id)
  if (index < 0) return null
  return <SpecVersionDiff previous={index > 0 ? versions[index - 1] : null} version={versions[index]} />
}

function WorklistDetail({ item }) {
  const { t } = useT("common")
  const template = useSelector((state) => limsWorklistTemplates.selectTemplate(state, item.id))
  if (!template) return null
  const steps = template.steps || []
  return (
    <div className="LimsChangeControl-detail-block">
      <p>
        <strong>{t("limsWorklistTemplateSteps")}:</strong>
      </p>
      <ol>
        {steps.map((step) => (
          <li key={step.id}>
            {step.instruction}
            {step.note ? ` — ${step.note}` : ""}
          </li>
        ))}
      </ol>
    </div>
  )
}

const SST_FIELDS = [
  ["rsdAreaMax", "limsSstRsdAreaMax"],
  ["rsdRtMax", "limsSstRsdRtMax"],
  ["tailingMin", "limsSstTailingMin"],
  ["tailingMax", "limsSstTailingMax"],
  ["plateCountMin", "limsSstPlateCountMin"],
  ["resolutionMin", "limsSstResolutionMin"],
  ["injectionCount", "limsSstInjectionCount"],
]

function SstDetail({ item }) {
  const { t } = useT("common")
  const template = useSelector((state) => limsSstTemplates.selectTemplate(state, item.id))
  if (!template) return null
  return (
    <div className="LimsChangeControl-detail-block">
      <p>
        <strong>{t("limsSstInstrumentType")}:</strong> {template.instrumentType}
      </p>
      <ul>
        {SST_FIELDS.filter(([key]) => template[key] !== null && template[key] !== undefined && template[key] !== "").map(
          ([key, labelKey]) => (
            <li key={key}>
              {t(labelKey)}: {String(template[key])}
            </li>
          )
        )}
      </ul>
    </div>
  )
}

function ForskriftDetail({ item }) {
  const { t } = useT("common")
  const dispatch = useDispatch()
  const doc = useSelector((state) => limsForskrifter.selectDocumentById(state, item.parentId))
  const [resolving, setResolving] = useState(false)
  const version = doc && (doc.versions || []).find((v) => v.id === item.id)
  if (!version) return null
  const file = version.file || {}

  const open = () => {
    if (resolving || !file.key) return
    setResolving(true)
    dispatch(limsForskrifter.getReadUrl(file.key))
      .then((url) => openPresignedFile(url))
      .catch(() => dispatch(message.error(t("limsForskriftDownloadError"))))
      .finally(() => setResolving(false))
  }

  return (
    <div className="LimsChangeControl-detail-block">
      <p>
        <strong>{t("limsForskriftFile")}:</strong> {file.name || "—"}
        {file.key && (
          <Button variant="link" size="sm" disabled={resolving} onClick={open} title={t("limsForskriftOpen")}>
            <FiExternalLink /> {t("limsForskriftOpen")}
          </Button>
        )}
      </p>
    </div>
  )
}
